import { subDays } from 'date-fns';
import type { CollectionStrategyItem } from '@/types/collection-strategies';

export function generateMockStrategies(): CollectionStrategyItem[] {
  return [
    {
      id: 'strategy-1',
      name: 'Standard 30-Day Collection',
      type: 'Standard',
      description: 'Standard collection workflow for accounts 30+ days overdue with moderate risk profile',
      status: 'Active',
      activeCasesCount: 45,
      successRate: 78.5,
      avgDaysToPayment: 18,
      trigger: {
        type: 'days_overdue',
        daysOverdue: 30,
      },
      actions: [
        {
          id: 'action-1',
          type: 'email',
          timing: {
            type: 'days_from_trigger',
            daysFromTrigger: 0,
          },
          templateName: 'Payment Reminder - Friendly',
          details: 'Send initial payment reminder with account summary',
          order: 1,
        },
        {
          id: 'action-2',
          type: 'phone_call',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 7,
          },
          details: 'Follow-up call to discuss payment options',
          order: 2,
        },
        {
          id: 'action-3',
          type: 'payment_link',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 3,
          },
          details: 'Send secure payment link via email',
          order: 3,
        },
      ],
      assignmentRules: [
        {
          criterion: 'days_overdue_range',
          enabled: true,
          config: {
            minValue: 30,
            maxValue: 60,
          },
        },
        {
          criterion: 'risk_profile',
          enabled: true,
          config: {
            selectedValues: ['Medium', 'Low'],
          },
        },
      ],
      aiBehavior: {
        communicationTone: 3,
        formalityLevel: 'professional',
        personalizationDepth: 'moderate',
        contextFactors: { paymentHistory: true, riskProfile: true, relationshipDuration: false, industryContext: false, economicFactors: false, previousDisputes: false, communicationPreferences: false },
        timingOptimization: true,
        escalationTriggers: { noResponseDays: 14, brokenPromises: 1, disputeRaised: false },
        humanReviewRequired: false,
      },
      performanceMetrics: {
        totalCasesProcessed: 234,
        successfulResolutions: 184,
        averageResolutionTime: 18,
        customerSatisfactionScore: 4.2,
        collectionRate: 12,
      },
      createdAt: subDays(new Date(), 90),
      updatedAt: subDays(new Date(), 5),
    },
    {
      id: 'strategy-2',
      name: 'Intensive High-Value Recovery',
      type: 'Intensive',
      description: 'Aggressive collection strategy for high-value accounts with significant overdue balances',
      status: 'Active',
      activeCasesCount: 23,
      successRate: 85.2,
      avgDaysToPayment: 12,
      trigger: {
        type: 'amount_threshold',
        amountThreshold: 10000,
      },
      actions: [
        {
          id: 'action-1',
          type: 'phone_call',
          timing: {
            type: 'days_from_trigger',
            daysFromTrigger: 0,
          },
          details: 'Immediate phone contact with decision maker',
          order: 1,
        },
        {
          id: 'action-2',
          type: 'email',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 1,
          },
          templateName: 'High Priority - Urgent Action Required',
          details: 'Send formal notice with payment deadline',
          order: 2,
        },
        {
          id: 'action-3',
          type: 'escalation',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 5,
          },
          details: 'Escalate to senior collections manager',
          order: 3,
        },
      ],
      assignmentRules: [
        {
          criterion: 'amount_range',
          enabled: true,
          config: {
            minValue: 10000,
          },
        },
        {
          criterion: 'risk_profile',
          enabled: true,
          config: {
            selectedValues: ['High', 'Medium'],
          },
        },
      ],
      aiBehavior: {
        communicationTone: 4,
        formalityLevel: 'formal',
        personalizationDepth: 'deep',
        contextFactors: { paymentHistory: true, riskProfile: true, relationshipDuration: true, industryContext: false, economicFactors: false, previousDisputes: false, communicationPreferences: false },
        timingOptimization: true,
        escalationTriggers: { noResponseDays: 7, brokenPromises: 1, disputeRaised: true },
        humanReviewRequired: true,
      },
      performanceMetrics: {
        totalCasesProcessed: 87,
        successfulResolutions: 74,
        averageResolutionTime: 12,
        customerSatisfactionScore: 3.8,
        collectionRate: 28,
      },
      createdAt: subDays(new Date(), 120),
      updatedAt: subDays(new Date(), 10),
    },
    {
      id: 'strategy-3',
      name: 'VIP Customer Care',
      type: 'VIP',
      description: 'White-glove collection approach for premium customers maintaining positive relationships',
      status: 'Active',
      activeCasesCount: 12,
      successRate: 92.3,
      avgDaysToPayment: 15,
      trigger: {
        type: 'days_overdue',
        daysOverdue: 45,
      },
      actions: [
        {
          id: 'action-1',
          type: 'email',
          timing: {
            type: 'days_from_trigger',
            daysFromTrigger: 0,
          },
          templateName: 'Courtesy Reminder - VIP',
          details: 'Personalized reminder from account manager',
          order: 1,
        },
        {
          id: 'action-2',
          type: 'phone_call',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 5,
          },
          details: 'Personal call from dedicated account manager',
          order: 2,
        },
        {
          id: 'action-3',
          type: 'payment_plan',
          timing: {
            type: 'conditional',
          },
          details: 'Offer flexible payment plan options',
          order: 3,
        },
      ],
      assignmentRules: [
        {
          criterion: 'customer_segment',
          enabled: true,
          config: {
            selectedValues: ['Enterprise', 'Strategic'],
          },
        },
        {
          criterion: 'relationship_type',
          enabled: true,
          config: {
            selectedValues: ['Long-term', 'High-value'],
          },
        },
      ],
      aiBehavior: {
        communicationTone: 2,
        formalityLevel: 'professional',
        personalizationDepth: 'deep',
        contextFactors: { paymentHistory: true, riskProfile: false, relationshipDuration: true, industryContext: false, economicFactors: false, previousDisputes: false, communicationPreferences: false },
        timingOptimization: true,
        escalationTriggers: { noResponseDays: 21, brokenPromises: 0, disputeRaised: false },
        humanReviewRequired: true,
      },
      performanceMetrics: {
        totalCasesProcessed: 52,
        successfulResolutions: 48,
        averageResolutionTime: 15,
        customerSatisfactionScore: 4.8,
        collectionRate: 5,
      },
      createdAt: subDays(new Date(), 150),
      updatedAt: subDays(new Date(), 3),
    },
    {
      id: 'strategy-4',
      name: 'High Risk Mitigation',
      type: 'High Risk',
      description: 'Rapid response protocol for accounts with elevated risk of non-payment',
      status: 'Active',
      activeCasesCount: 18,
      successRate: 65.8,
      avgDaysToPayment: 25,
      trigger: {
        type: 'risk_score',
        riskScoreThreshold: 75,
      },
      actions: [
        {
          id: 'action-1',
          type: 'phone_call',
          timing: {
            type: 'days_from_trigger',
            daysFromTrigger: 0,
          },
          details: 'Immediate contact attempt',
          order: 1,
        },
        {
          id: 'action-2',
          type: 'email',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 2,
          },
          templateName: 'Urgent - Account Review Required',
          details: 'Formal notice with account hold warning',
          order: 2,
        },
        {
          id: 'action-3',
          type: 'account_action',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 7,
          },
          details: 'Place account on credit hold',
          order: 3,
        },
        {
          id: 'action-4',
          type: 'escalation',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 5,
          },
          details: 'Escalate to legal review',
          order: 4,
        },
      ],
      assignmentRules: [
        {
          criterion: 'risk_profile',
          enabled: true,
          config: {
            selectedValues: ['High'],
          },
        },
        {
          criterion: 'payment_behavior',
          enabled: true,
          config: {
            selectedValues: ['Consistently Late', 'Broken Promises'],
          },
        },
      ],
      aiBehavior: {
        communicationTone: 5,
        formalityLevel: 'formal',
        personalizationDepth: 'minimal',
        contextFactors: { paymentHistory: true, riskProfile: true, relationshipDuration: false, industryContext: false, economicFactors: false, previousDisputes: false, communicationPreferences: false },
        timingOptimization: false,
        escalationTriggers: { noResponseDays: 5, brokenPromises: 1, disputeRaised: true },
        humanReviewRequired: true,
      },
      performanceMetrics: {
        totalCasesProcessed: 145,
        successfulResolutions: 95,
        averageResolutionTime: 25,
        customerSatisfactionScore: 2.9,
        collectionRate: 45,
      },
      createdAt: subDays(new Date(), 180),
      updatedAt: subDays(new Date(), 7),
    },
    {
      id: 'strategy-5',
      name: 'Early Intervention',
      type: 'Standard',
      description: 'Proactive outreach for accounts showing early signs of payment delays',
      status: 'Active',
      activeCasesCount: 67,
      successRate: 88.7,
      avgDaysToPayment: 8,
      trigger: {
        type: 'payment_pattern_change',
      },
      actions: [
        {
          id: 'action-1',
          type: 'email',
          timing: {
            type: 'days_from_trigger',
            daysFromTrigger: 0,
          },
          templateName: 'Friendly Check-in',
          details: 'Proactive outreach before account becomes overdue',
          order: 1,
        },
        {
          id: 'action-2',
          type: 'phone_call',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 5,
          },
          details: 'Courtesy call to ensure no issues',
          order: 2,
        },
      ],
      assignmentRules: [
        {
          criterion: 'payment_behavior',
          enabled: true,
          config: {
            selectedValues: ['Pattern Change Detected'],
          },
        },
        {
          criterion: 'days_overdue_range',
          enabled: true,
          config: {
            minValue: 0,
            maxValue: 15,
          },
        },
      ],
      aiBehavior: {
        communicationTone: 2,
        formalityLevel: 'professional',
        personalizationDepth: 'deep',
        contextFactors: { paymentHistory: true, riskProfile: false, relationshipDuration: true, industryContext: false, economicFactors: false, previousDisputes: false, communicationPreferences: false },
        timingOptimization: true,
        escalationTriggers: { noResponseDays: 14, brokenPromises: 0, disputeRaised: false },
        humanReviewRequired: false,
      },
      performanceMetrics: {
        totalCasesProcessed: 312,
        successfulResolutions: 277,
        averageResolutionTime: 8,
        customerSatisfactionScore: 4.6,
        collectionRate: 8,
      },
      createdAt: subDays(new Date(), 60),
      updatedAt: subDays(new Date(), 2),
    },
    {
      id: 'strategy-6',
      name: 'Broken Promise Follow-up',
      type: 'Intensive',
      description: 'Targeted strategy for accounts that have broken payment promises',
      status: 'Active',
      activeCasesCount: 15,
      successRate: 72.4,
      avgDaysToPayment: 20,
      trigger: {
        type: 'promise_broken',
      },
      actions: [
        {
          id: 'action-1',
          type: 'phone_call',
          timing: {
            type: 'days_from_trigger',
            daysFromTrigger: 1,
          },
          details: 'Immediate follow-up on broken promise',
          order: 1,
        },
        {
          id: 'action-2',
          type: 'email',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 2,
          },
          templateName: 'Promise Follow-up - Firm',
          details: 'Formal reminder of commitment',
          order: 2,
        },
        {
          id: 'action-3',
          type: 'escalation',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 7,
          },
          details: 'Escalate to collections manager',
          order: 3,
        },
      ],
      assignmentRules: [
        {
          criterion: 'payment_behavior',
          enabled: true,
          config: {
            selectedValues: ['Broken Promises'],
          },
        },
      ],
      aiBehavior: {
        communicationTone: 4,
        formalityLevel: 'formal',
        personalizationDepth: 'moderate',
        contextFactors: { paymentHistory: true, riskProfile: false, relationshipDuration: false, industryContext: false, economicFactors: false, previousDisputes: false, communicationPreferences: false },
        timingOptimization: true,
        escalationTriggers: { noResponseDays: 7, brokenPromises: 2, disputeRaised: false },
        humanReviewRequired: true,
      },
      performanceMetrics: {
        totalCasesProcessed: 98,
        successfulResolutions: 71,
        averageResolutionTime: 20,
        customerSatisfactionScore: 3.2,
        collectionRate: 35,
      },
      createdAt: subDays(new Date(), 45),
      updatedAt: subDays(new Date(), 1),
    },
    {
      id: 'strategy-7',
      name: 'Post-Dispute Resolution',
      type: 'Standard',
      description: 'Collection workflow for accounts after dispute resolution',
      status: 'Inactive',
      activeCasesCount: 0,
      successRate: 81.5,
      avgDaysToPayment: 14,
      trigger: {
        type: 'manual_only',
      },
      actions: [
        {
          id: 'action-1',
          type: 'email',
          timing: {
            type: 'days_from_trigger',
            daysFromTrigger: 0,
          },
          templateName: 'Dispute Resolved - Payment Request',
          details: 'Notify customer of resolution and request payment',
          order: 1,
        },
        {
          id: 'action-2',
          type: 'payment_link',
          timing: {
            type: 'days_from_previous',
            daysFromPrevious: 3,
          },
          details: 'Send payment link with adjusted amount',
          order: 2,
        },
      ],
      assignmentRules: [
        {
          criterion: 'payment_behavior',
          enabled: true,
          config: {
            selectedValues: ['Dispute Resolved'],
          },
        },
      ],
      performanceMetrics: {
        totalCasesProcessed: 54,
        successfulResolutions: 44,
        averageResolutionTime: 14,
        customerSatisfactionScore: 4.1,
        collectionRate: 15,
      },
      createdAt: subDays(new Date(), 200),
      updatedAt: subDays(new Date(), 30),
    },
  ];
}
